import { useEffect, useRef, useState } from 'react';
import { ArrowDownTrayIcon, ChevronDownIcon } from '@heroicons/react/16/solid';
import { slugifyTrackingValue, trackEvent } from '@/utils/analytics';

const DOWNLOAD_FORMATS = [
  { label: 'TSV', value: 'tsv' },
  { label: 'Excel', value: 'xlsx' },
] as const;

export type TableHeaderDownloadExtension = {
  label: string;
  value: string;
};

type TableHeaderDownloadProps = {
  onDownload: (extension: TableHeaderDownloadExtension) => void;
  /** Plan limit microcopy, e.g. "10 rows" for free / unauthenticated users. */
  limitLabel?: string | null;
  disabled?: boolean;
};

export function TableHeaderDownload({
  onDownload,
  limitLabel = null,
  disabled = false,
}: TableHeaderDownloadProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const label = limitLabel ? `Download · ${limitLabel}` : 'Download';
  const title = limitLabel
    ? `Download results (limited to ${limitLabel} on your current plan)`
    : 'Download results as TSV or Excel';

  return (
    <div className="relative inline-flex" ref={rootRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          trackEvent({
            name: 'download_result',
            buttonId: 'search_table_header_download_open',
            label,
            page: 'search_results',
            section: 'table_header',
            feature: 'download',
            eventGroup: 'search',
            eventCategory: 'search',
          });
          setOpen(prev => !prev);
        }}
        title={title}
        aria-label={title}
        aria-haspopup="menu"
        aria-expanded={open}
        className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-semibold text-slate-600 hover:bg-slate-100 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-50"
        data-testid="table-header-download"
      >
        <ArrowDownTrayIcon className="h-3.5 w-3.5 shrink-0" aria-hidden />
        <span className="whitespace-nowrap">{label}</span>
        <ChevronDownIcon
          className="h-3 w-3 shrink-0 transition-transform duration-150"
          style={{ transform: open ? 'rotate(180deg)' : undefined }}
          aria-hidden
        />
      </button>
      {open && (
        <div
          role="menu"
          aria-label="Download format"
          className="absolute left-0 top-full z-[1000] mt-1 min-w-[9rem] overflow-hidden rounded-md border border-slate-200 bg-white py-1 shadow-lg"
        >
          {DOWNLOAD_FORMATS.map(extension => (
            <button
              key={extension.value}
              type="button"
              role="menuitem"
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-semibold text-slate-700 hover:bg-slate-50"
              onClick={() => {
                trackEvent({
                  name: 'download_result',
                  buttonId: `search_table_header_download_${slugifyTrackingValue(extension.label)}`,
                  label: `Download ${extension.label}`,
                  page: 'search_results',
                  section: 'table_header',
                  feature: 'download',
                  eventGroup: 'search',
                  eventCategory: 'search',
                });
                onDownload(extension);
                setOpen(false);
              }}
            >
              <ArrowDownTrayIcon className="h-3.5 w-3.5" aria-hidden />
              {extension.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
